import {
    Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography
} from "@mui/material";
import { useState } from "react";
import { api } from "../../../api/API";
import { Task } from "../../../api/Tasks";
import { SnackCancel, snackText, useSnackAction } from "../../../misc/SnackAction";
import { teamName, useWorkspace } from "../../../misc/Workspace";

type Pending = {
    task: Task;
    team: number;

    // linked counts the tasks a cascade takes along, the task itself left out
    linked: number;
    warnings: string[];
}

// useMoveTask puts a task on another team. A task with links asks whether they go
// too, and the user is warned before a project is unlinked or assignees are removed.
export function useMoveTask(props: { onMoved: () => void }) {
    const ws = useWorkspace();
    const [pending, setPending] = useState<Pending | null>(null);

    const move = useSnackAction(async (input: { task: Task; team: number; cascade: boolean }) => {
        setPending(null);

        const result = await api.tasks.move({ id: input.task.id, team: input.team, cascade: input.cascade });
        props.onMoved();

        const parts = [result.moved === 1 ? "Moved 1 task" : "Moved " + result.moved + " tasks"];
        if (result.unlinkedFromProject) parts.push(result.unlinkedFromProject + " unlinked from their project");
        if (result.unassigned) parts.push(result.unassigned + " assignees removed");

        return snackText(parts.join(". "));
    }, { success: "Moved" });

    // what the move would do is read first, so the user can be asked about it
    const start = useSnackAction(async (input: { task: Task; team: number }) => {
        const task = input.task;
        const hasLinks = task.blockedBy.length + task.followedBy.length > 0;

        const [linked, members] = await Promise.all([
            hasLinks ? api.tasks.list({ linkedTo: task.id }) : Promise.resolve([] as Task[]),
            api.teams.listMembers({ team: input.team }),
        ]);

        const stays = new Set(members.members.map(member => member.usr));
        const removed = task.assignedTo.filter(a => !stays.has(a.usr));

        const warnings: string[] = [];
        if (task.project) warnings.push("The task is taken out of " + (task.projectName || "its project") + ", which stays on its team.");
        if (removed.length === 1) warnings.push(removed[0].name + " isn't on the new team, so is removed as an assignee.");
        if (removed.length > 1) warnings.push(removed.map(a => a.name).join(", ") + " aren't on the new team, so are removed as assignees.");

        if (!hasLinks && warnings.length === 0) {
            move.callback({ task: task, team: input.team, cascade: false });
            return SnackCancel;
        }

        setPending({
            task: task,
            team: input.team,
            linked: linked.filter(row => row.id !== task.id).length,
            warnings: warnings,
        });

        return SnackCancel;
    }, { success: "" });

    const target = pending ? ws.teams.find(team => team.id === pending.team) || null : null;

    return {
        start: start.callback,
        loading: start.loading || move.loading,
        element: (
            <Dialog open={!!pending} onClose={() => setPending(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Move to {teamName(target, pending?.team || 0)}?</DialogTitle>
                <DialogContent>
                    <Stack spacing={1}>
                        {pending?.warnings.map(text => <Alert key={text} severity="warning">{text}</Alert>)}
                        {pending && pending.linked > 0 && (
                            <Typography variant="body2">
                                This task is linked to {pending.linked} other {pending.linked === 1 ? "task" : "tasks"}.
                                Tasks you can't see stay where they are.
                            </Typography>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
                    <Button onClick={() => setPending(null)}>Cancel</Button>
                    <Button variant={pending && pending.linked > 0 ? "outlined" : "contained"}
                            onClick={() => pending && move.callback({ task: pending.task, team: pending.team, cascade: false })}>
                        Move only this task
                    </Button>
                    {pending && pending.linked > 0 && (
                        <Button variant="contained"
                                onClick={() => move.callback({ task: pending.task, team: pending.team, cascade: true })}>
                            Move this task and {pending.linked} linked
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        ),
    };
}
